import type { ReactElement, ReactNode } from "react";
import React, { createContext, useContext, useMemo } from "react";
import { IANAZone } from "luxon";

type ContextValue = {
	readonly timezone: string;
};

const Context = createContext<ContextValue | undefined>(undefined);

type TimezoneProviderProps = {
	readonly timezone: string;
	readonly children: ReactNode;
};

export function TimezoneProvider({
	timezone,
	children,
}: TimezoneProviderProps): ReactElement {
	const validatedTimezone = useMemo(() => {
		const zone = IANAZone.create(timezone);
		if (!zone.isValid) {
			throw new Error(
				`Invalid timezone: ${timezone}. Please use a valid timezone name.`,
			);
		}
		return timezone;
	}, [timezone]);

	const value = useMemo(
		() => ({
			timezone: validatedTimezone,
		}),
		[validatedTimezone],
	);

	return <Context.Provider value={value}>{children}</Context.Provider>;
}

function useTimezone(): string {
	const context = useContext(Context);
	if (!context) {
		throw new Error("No timezone context");
	}
	return context.timezone;
}

export { useTimezone };
